import {Text} from '@mantine/core'
import type {RecommendationSourceType} from '@package/shared'
import {formatTime} from './formatter/formatTime.ts'
import type {Dayjs} from 'dayjs'
import {SourceBadge} from './components/badges/SourceBadge.tsx'
import {ProjectBadge} from './components/badges/ProjectBadge.tsx'
import {PartlyCollapse} from './components/PartlyCollapse.tsx'
import {CoffeeBadge} from './components/badges/CoffeeBadge.tsx'
import type {ReactNode} from 'react'
import {MergedBadge} from './components/badges/MergedBadge.tsx'
import {useUserSettings} from '../../providers/userSettings/useUserSettings.ts'

type Props = {
  start: Dayjs
  end: Dayjs
  breakHours: number
  description: string | undefined | null
  sourceType: RecommendationSourceType | undefined | null
  projectId: string | undefined | null
  merged: boolean
  onMergedBadgeClick?: () => void
  disableCompactMode?: boolean
  compact?: boolean
}

export const TimeEntryContainer = ({
  start,
  end,
  breakHours,
  description,
  sourceType,
  projectId,
  merged,
  onMergedBadgeClick,
  compact = false,
}: Props) => {
  const {userSettings} = useUserSettings()
  const badges: ReactNode[] = []
  if (projectId) badges.push(<ProjectBadge key={'project'} projectId={projectId} />)
  if (sourceType) badges.push(<SourceBadge key={'source'} type={sourceType} />)
  if (merged) badges.push(<MergedBadge key={'merged'} onClick={onMergedBadgeClick} />)
  if (breakHours > 0) badges.push(<CoffeeBadge key={'break'} breakHours={breakHours} />)

  return (
    <>
      <div style={{display: 'flex', gap: 5}}>
        <Text size={compact ? 'sm' : undefined}>
          {formatTime(start, userSettings)} - {formatTime(end, userSettings)}
        </Text>
        {compact && projectId && <ProjectBadge projectId={projectId} compact />}
        {compact && breakHours > 0 && <CoffeeBadge breakHours={breakHours} compact />}
      </div>
      {!compact && (
        <>
          {description && (
            <PartlyCollapse minHeight={65}>
              <Text size={'sm'} c={'dimmed'} style={{whiteSpace: 'pre-line'}}>
                {description}
              </Text>
            </PartlyCollapse>
          )}
          {badges.length > 0 && <div style={{display: 'flex', gap: 5}}>{badges}</div>}
        </>
      )}
    </>
  )
}
